import { useQueryClient } from '@tanstack/react-query';
import { Form, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { updateCustomer, type Customer, type DuplicateMatch, type UpdateCustomerInput } from './api';
import { CustomerFormFields, type CustomerFormValues } from './CustomerFormFields';
import { DuplicateWarning } from './DuplicateWarning';

const { Text } = Typography;

function initialValues(customer: Customer): CustomerFormValues {
  const v = (s: string | null): string | undefined => s ?? undefined;
  return {
    name: customer.name,
    phone: customer.phone,
    email: v(customer.email),
    idType: v(customer.idType),
    idNumber: v(customer.idNumber),
    tin: v(customer.tin),
    addressLine1: v(customer.addressLine1),
    postcode: v(customer.postcode),
    city: v(customer.city),
    state: v(customer.state),
    notes: v(customer.notes),
  };
}

/** Blank means "none": the API clears a field that is left out. */
function blankToUndefined(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Builds the PATCH body.
 *
 * The edit is a full replacement, so fields this form does not show (other
 * phone, second address line, MSIC) are carried over from the record rather
 * than silently cleared. The IC is the exception the other way round: it is
 * sent only when the user changed it, because the form holds the MASKED value
 * and sending that back would overwrite the real number with asterisks.
 */
export function buildUpdate(
  customer: Customer,
  values: CustomerFormValues,
  confirmNotDuplicate: boolean,
): UpdateCustomerInput {
  const optional: Record<string, string | undefined> = {
    altPhone: blankToUndefined(customer.altPhone),
    msicCode: blankToUndefined(customer.msicCode),
    addressLine2: blankToUndefined(customer.addressLine2),
    email: blankToUndefined(values.email),
    idType: blankToUndefined(values.idType),
    tin: blankToUndefined(values.tin),
    addressLine1: blankToUndefined(values.addressLine1),
    postcode: blankToUndefined(values.postcode),
    city: blankToUndefined(values.city),
    state: blankToUndefined(values.state),
    notes: blankToUndefined(values.notes),
  };
  const input: UpdateCustomerInput = {
    name: values.name.trim(),
    phone: values.phone.trim(),
    ...Object.fromEntries(Object.entries(optional).filter(([, value]) => value !== undefined)),
  };

  const typedId = (values.idNumber ?? '').trim();
  if (typedId !== (customer.idNumber ?? '')) input.idNumber = typedId;

  if (confirmNotDuplicate) input.confirmNotDuplicate = true;
  return input;
}

export function CustomerEditDrawer({
  customer,
  open,
  onClose,
  onOpenMatch,
}: {
  customer: Customer;
  open: boolean;
  onClose: () => void;
  onOpenMatch: (uid: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CustomerFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // Re-seed from the record every time the drawer opens, so a cancelled edit
  // does not leave half-typed values behind for the next one.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues(customer));
      setDuplicates(null);
      setFailure(null);
    }
  }, [open, customer, form]);

  const submit = async (force: boolean): Promise<void> => {
    let values: CustomerFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // The form already shows what is wrong next to each field.
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      await updateCustomer(customer.uid, buildUpdate(customer, values, force));
      await queryClient.invalidateQueries({ queryKey: ['customer', customer.uid] });
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDuplicates(null);
      void message.success(t('customers.edit.saved'));
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CUSTOMER_DUPLICATE_SUSPECTED') {
        setDuplicates(e.details['matches'] as DuplicateMatch[]);
        return;
      }
      setFailure(e instanceof ApiError ? e.message : t('customers.edit.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('customers.edit.title')}
      submitLabel={t('common.save')}
      submitting={submitting}
      onSubmit={() => void submit(false)}
      onClose={onClose}
      error={
        duplicates !== null ? (
          <DuplicateWarning
            matches={duplicates}
            onOpenMatch={onOpenMatch}
            onConfirmDifferent={() => void submit(true)}
          />
        ) : failure !== null ? (
          <Text type="danger">{failure}</Text>
        ) : undefined
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <CustomerFormFields mode="edit" storedIdNumber={customer.idNumber} />
      </Form>
    </ActionDrawer>
  );
}
